import { CloudUploadOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Input, Space } from 'antd';
import { useTranslation } from 'react-i18next';

import { StoreListCounts, StoreListFilter } from 'models/Store';

import './StoreFilters.scss';

const { Search } = Input;

interface StoreFiltersProps {
  term: string;
  filter: StoreListFilter;
  counts: StoreListCounts;
  loading?: boolean;
  onTermChange: (term: string) => void;
  onFilterChange: (filter: StoreListFilter) => void;
  onBulkUpload: () => void;
  onCreate: () => void;
}

const FILTER_KEYS: { key: StoreListFilter; countKey: keyof StoreListCounts }[] =
  [
    { key: 'ALL', countKey: 'all' },
    { key: 'CAPITAL', countKey: 'capital' },
    { key: 'REGION', countKey: 'region' },
    { key: 'VIP', countKey: 'vip' },
    { key: 'INACTIVE', countKey: 'inactive' },
  ];

function StoreFilters({
  term,
  filter,
  counts,
  loading,
  onTermChange,
  onFilterChange,
  onBulkUpload,
  onCreate,
}: StoreFiltersProps) {
  const { t } = useTranslation();
  const i18nBase = 'scenes.stores.scene';

  return (
    <div className="store-filters">
      <div className="store-filters__header">
        <div className="store-filters__titles">
          <h1 className="store-filters__title">{t(`${i18nBase}.title`)}</h1>
          <p className="store-filters__description">
            {t(`${i18nBase}.description`)}
          </p>
        </div>
        <Space wrap className="store-filters__actions">
          <Button icon={<CloudUploadOutlined />} onClick={onBulkUpload}>
            {t(`${i18nBase}.buttons.bulk_upload`)}
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={onCreate}>
            {t(`${i18nBase}.buttons.create`)}
          </Button>
        </Space>
      </div>

      <Card className="store-filters__card" bordered>
        <div className="store-filters__search-row">
          <span className="store-filters__search-label">
            {t(`${i18nBase}.filters.label`)}
          </span>
          <Search
            allowClear
            disabled={loading}
            className="store-filters__search-input"
            placeholder={t(`${i18nBase}.filters.search_placeholder`)}
            value={term}
            onChange={(e) => onTermChange(e.target.value)}
            onSearch={(value) => onTermChange(value)}
          />
        </div>

        <div className="store-filters__pills" role="tablist">
          {FILTER_KEYS.map(({ key, countKey }) => {
            const active = filter === key;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={active}
                className={`store-filters__pill${
                  active ? ' store-filters__pill--active' : ''
                }`}
                disabled={loading}
                onClick={() => onFilterChange(key)}
              >
                <span className="store-filters__pill-label">
                  {t(`${i18nBase}.filters.pills.${key.toLowerCase()}`)}
                </span>
                <span className="store-filters__pill-count">
                  {counts[countKey]}
                </span>
              </button>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

export default StoreFilters;
